/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { observer } from 'mobx-react';
import React, { Component } from 'react';
import './layout.scss';
import Play from '@react/react-spectrum/Icon/Play';
import uuid from 'uuid';
import Config from '../../stores/config';
import TranslationUtil from '../../utils/translation';
import { interpretKey } from '../../utils/eventUtil';

let videoAutoPlayed;

const renderLayout = ({
    videoList, analytics, appConfig, appsSkeletonModel, onAction,
}) => {
    /**
     * send video play event
     */
    const { bannerAnalyticsCallback } = analytics;
    bannerAnalyticsCallback('videoPlayed', { analytics }, appConfig, appsSkeletonModel);
    videoAutoPlayed = true;
    const onCloseCallback = () => {
        bannerAnalyticsCallback('videoClosed', { analytics }, appConfig, appsSkeletonModel);
    };
    const onPauseCallback = () => {
        bannerAnalyticsCallback('videoControlEvent', { analytics, type: 'pause' }, appConfig, appsSkeletonModel);
    };
    const onPlayCallback = () => {
        if (!videoAutoPlayed) {
            bannerAnalyticsCallback('videoControlEvent', { analytics, type: 'resume' }, appConfig, appsSkeletonModel);
        } else {
            videoAutoPlayed = false;
        }
    };
    const onLoadStartCallback = () => {
        const isMobileBreakPoint = window && window.matchMedia && !window.matchMedia('(min-width: 768px)').matches;
        if (Config.isNonDownloadableSurface && isMobileBreakPoint) {
            const vidElem = document.getElementsByClassName('banner-video')[0];
            if (vidElem.requestFullscreen) {
                vidElem.requestFullscreen();
            } else if (vidElem.mozRequestFullScreen) {
                /* Firefox */
                vidElem.mozRequestFullScreen();
            } else if (vidElem.webkitRequestFullscreen) {
                /* Chrome, Safari and Opera */
                vidElem.webkitRequestFullscreen();
            } else if (vidElem.msRequestFullscreen) {
                /* IE/Edge */
                vidElem.msRequestFullscreen();
            }
        }
    };
    onAction({
        videoList,
        callbacks: {
            onCloseCallback,
            onPauseCallback,
            onPlayCallback,
            onLoadStartCallback,
        },
        config: {
            disablePictureInPicture: 'true',
        },
    });
};

@observer
class VideoOnlyLayout extends Component {
    renderGuid = uuid.v4();

    componentDidMount() {
        const { bannerItem, appConfig, appsSkeletonModel } = this.props;
        const {
            analytics,
            analytics: { bannerAnalyticsCallback },
        } = bannerItem;

        bannerAnalyticsCallback('bannerRendered',
            {
                analytics,
                CTAText: null,
                isVideo: true,
                renderGuid: this.renderGuid,
            },
            appConfig,
            appsSkeletonModel);
    }

    render() {
        const { bannerItem, appConfig, appsSkeletonModel } = this.props;
        const {
            thumbNailImg,
            videoList,
            analytics,
            appName,
            onAction,
        } = bannerItem;
        return (
            <div className="video-only-layout" render-guid={this.renderGuid}>
                <div
                    role="button"
                    aria-label={`${appName} ${TranslationUtil.getMessage('ccx.banner.video.video')}`}
                    className="play-icon-container"
                    onClick={() => renderLayout({
                        videoList, analytics, appConfig, appsSkeletonModel, onAction,
                    })}
                    tabIndex="0"
                    onKeyDown={interpretKey({
                        onEnter: [() => renderLayout({
                            videoList, analytics, appConfig, appsSkeletonModel, onAction,
                        }), true],
                    })}>
                    <Play size="XL" className="play-icon" />
                </div>
                <div className="banner-image" style={{ backgroundImage: `url('${thumbNailImg}')` }} />
            </div>
        );
    }
}

export default VideoOnlyLayout;
